import type { Dictionary } from './dictionaryTypes';
import { EN } from './en';

export const ES: Dictionary = {
    ...EN,
    localeLabel: 'Español',
    languages: {
        ru: 'Ruso',
        en: 'Inglés',
        es: 'Español',
        fr: 'Francés',
        ar: 'Árabe',
        'zh-CN': 'Chino mandarín',
        'zh-HK': 'Chino cantonés',
    },
    common: {
        ...EN.common,
        close: 'Cerrar',
        done: 'Listo',
        settings: 'Ajustes',
        preferences: 'Preferencias',
        logout: 'Cerrar sesión',
        copied: 'Copiado!',
        backToDashboard: 'Volver al panel',
        backToChats: 'Volver a chats',
        backToRecordings: 'Volver a grabaciones',
        login: 'Entrar',
        forInvestors: 'Para inversores',
        learnMore: 'Más información',
        chats: 'Chats',
        email: 'Correo',
        openCall: 'Abrir llamada',
        currentBrowser: 'Navegador actual',
        anotherDevice: 'Otro dispositivo',
        thisBrowser: 'Este navegador',
        moreActions: 'Más acciones',
    },
    appError: {
        title: 'Algo salió mal',
        description: 'El error fue registrado. Recarga la página para intentarlo de nuevo.',
        retry: 'Reintentar',
    },
    offline: {
        eyebrow: 'Sin conexión',
        title: 'No hay conexión',
        description:
            'Puedes volver a abrir pantallas en caché de Chattenger, pero los mensajes nuevos, llamadas, correo y sincronización necesitan conexión.',
        openChats: 'Abrir chats',
    },
    compatibilityRoutes: {
        ...EN.compatibilityRoutes,
        errorEyebrow: 'Ruta de error',
        errorTitle: 'La superficie compartida de error ahora se renderiza a través de Next.js.',
        errorDescription: 'Así esta ruta sigue disponible mientras el resto del producto abandona la SPA retirada.',
        towerEyebrow: 'Ruta de compatibilidad',
        towerTitle: 'Tower ahora se resuelve a través del shell de Next.js.',
        towerDescription:
            'El acceso al juego seguirá disponible durante el cambio de SPA a Next. La ruta queda reservada y ya no depende del bundle retirado.',
        backHome: 'Volver a Chattenger',
        joinEyebrow: 'Unirse',
        joinInvalidTitle: 'El enlace de invitación no es válido',
        joinInvalidDescription: 'Este enlace de invitación está mal formado. Ábrelo de nuevo en Chattenger o pide un enlace nuevo.',
        openInChattenger: 'Abrir en Chattenger',
        continueOnWeb: 'Continuar en la web',
        joinDirectChatTitle: 'No se pudo abrir el chat directo',
        joinDirectChatDescription:
            'Se resolvió el usuario, pero ahora mismo no se pudo inicializar el chat del navegador. Intenta abrirlo en la aplicación.',
        goToDashboard: 'Ir al panel',
        joinChannelEyebrow: 'Unirse al canal',
        joinGroupEyebrow: 'Unirse al grupo',
        joinUnsupportedChannelDescription: 'La web resolvió este canal, pero todavía no está disponible en tu lista actual de chats del navegador.',
        joinUnsupportedGroupDescription: 'La web resolvió este grupo, pero todavía no está disponible en tu lista actual de chats del navegador.',
        joinUnsupportedChannelFallback: 'Abre este canal en Chattenger para verlo o completar el acceso o la suscripción.',
        joinUnsupportedGroupFallback: 'Abre este grupo en Chattenger para verlo o completar el acceso o la suscripción.',
        joinNotFoundTitle: 'Invitación no encontrada',
        joinNotFoundDescription: 'No se pudo resolver esta invitación con la cuenta actual. Puede ser inválida, privada o ya no estar disponible.',
    },
    theme: {
        ...EN.theme,
        system: 'Sistema',
        light: 'Claro',
        dark: 'Oscuro',
        label: 'Tema',
        hint: 'El tema se guarda solo en este navegador.',
    },
    settings: {
        ...EN.settings,
        eyebrow: 'Perfil y ajustes',
        title: 'Preferencias de interfaz',
        description: 'El idioma y el tema permanecen en este navegador para que la experiencia se sienta familiar cada vez que vuelves.',
        appearanceTab: 'Apariencia',
        devicesTab: 'Dispositivos',
        languageTab: 'Idioma',
        securityTab: 'Seguridad',
        appearanceTitle: 'Modo visual',
        appearanceCopy: 'Cambia la paleta al instante sin recargar. El cliente web debe mantenerse rápido y estable en cualquier pantalla.',
        languageTitle: 'Idioma de la interfaz',
        languageCopy: 'La localización se actualiza de inmediato en las superficies clave del cliente web.',
        securityTitle: 'Acceso de confianza',
        securityCopy:
            'Aprueba dispositivos nuevos, revoca navegadores antiguos y revisa invitaciones históricas de registro móvil desde una sesión confiable.',
        profileTitle: 'Perfil actual',
        profileCopy: 'Ajustes para este usuario y este navegador.',
        publicCopy: 'Las páginas públicas también guardan el tema y el idioma en este dispositivo.',
        photoAction: 'Foto',
        photoActionAria: 'Elegir la foto del perfil',
        photoUploadFailed: 'No se pudo subir la foto.',
        navigationTitle: 'Secciones',
        languageLabel: 'Idioma',
        languageHint: 'Ahora se admiten ruso, inglés, español, francés, árabe, chino mandarín y chino cantonés.',
        localeBadge: 'Idioma',
        themeBadge: 'Tema',
        focusHighlightsTitle: 'Resaltado de foco',
        focusHighlightsCopy:
            'Desactiva los resaltados visuales de foco en este navegador si resultan molestos durante el uso normal con ratón o táctil.',
        focusHighlightsLabel: 'Resaltado de foco',
        focusHighlightsOn: 'Activado',
        focusHighlightsOff: 'Desactivado',
        securityLoadFailed: 'No se pudo cargar la superficie de seguridad.',
        securityActionFailed: 'No se pudo completar la acción de seguridad.',
        securityDateUnavailable: 'No disponible',
        currentDeviceCannotBeRevoked: 'Este navegador es tu dispositivo de confianza actual y no puede revocarse a sí mismo.',
        devicesTitle: 'Dispositivos conectados',
        devicesCopy:
            'Mira cada teléfono, navegador y sesión de escritorio conectados a esta cuenta. El estado en línea se actualiza por socket autenticado.',
        devicesLoadFailed: 'No se pudieron cargar los dispositivos conectados.',
        devicesActionFailed: 'No se pudo actualizar la sesión del dispositivo.',
        devicesEmpty: 'Todavía no se encontraron dispositivos para esta cuenta.',
        devicesCurrent: 'Dispositivo actual',
        devicesView: 'Ver',
        devicesEndSession: 'Cerrar sesión',
        devicesEndingSession: 'Cerrando…',
        devicesLoggedInAt: 'Inicio',
        devicesUpdatedAt: 'Última actualización',
        devicesActiveSockets: 'Sockets activos',
        devicesUserAgent: 'User agent',
        devicesStatusOnline: 'En línea',
        devicesStatusOffline: 'Sin conexión',
        devicesStatusSignedOut: 'Sesión cerrada',
        deviceInspectionBack: 'Volver a dispositivos',
        deviceInspectionCopy: 'Revisa la actividad reciente de este dispositivo. Expande un evento para ver metadatos.',
        deviceInspectionEmpty: 'Aún no hay actividad reciente.',
        deviceActivityNoMetadata: 'Sin metadatos',
        deviceViewRequest: 'Solicitar vista en vivo',
        deviceViewRequestMicrophone: 'Solo micrófono',
        deviceViewRequestCameraMic: 'Cámara + micrófono',
        deviceViewPermissionSummary: 'Permisos del destino: micrófono {microphone}, cámara {camera}.',
        deviceViewPermissionGranted: 'concedido',
        deviceViewPermissionPrompt: 'pedirá permiso',
        deviceViewPermissionDenied: 'bloqueado',
        deviceViewPermissionUnknown: 'desconocido',
        deviceViewPermissionUnsupported: 'no compatible',
        deviceViewPermissionHint: 'Elige opciones ya concedidas para evitar un aviso de permisos en ese dispositivo.',
        deviceViewIdle: 'No hay solicitud de vista activa.',
        deviceViewPending: 'Esperando que el otro dispositivo inicie cámara y micrófono.',
        deviceViewStarting: 'Iniciando cámara y micrófono.',
        deviceViewStreaming: 'La cámara y el micrófono están transmitiendo.',
        deviceViewPermissionRequired: 'Se requiere permiso de cámara o micrófono en ese dispositivo.',
        deviceViewFailed: 'La vista en vivo falló.',
        deviceViewStopped: 'La vista en vivo se detuvo.',
        deviceViewStreamingChip: '{count} dispositivo(s) viendo cámara y micrófono',
        deviceViewAccept: 'Aprobar',
        deviceViewDecline: 'Rechazar',
        deviceViewPromptTitle: '¿Permitir vista del dispositivo?',
        deviceViewPromptBody: 'Otro dispositivo de la cuenta pide ver este navegador. Aprueba solo si lo solicitaste.',
        deviceViewRespondFailed: 'No se pudo responder a la solicitud de vista del dispositivo.',
        deviceViewOverlayTitle: 'Vista del dispositivo en vivo',
        deviceViewOverlayCopy: 'Usa este panel para ver el estado de la vista y la actividad segura del dispositivo seleccionado.',
        deviceViewOverlayMediaPending: 'El flujo de cámara/micrófono aparecerá aquí cuando el otro dispositivo empiece a transmitir.',
        deviceViewOverlayAudioActive: 'Flujo de audio activo',
        deviceViewOverlayLogsTitle: 'Flujo de actividad',
        pendingApprovalsTitle: 'Aprobaciones de dispositivos pendientes',
        pendingApprovalsCopy: 'Estos intentos de inicio de sesión esperan aprobación desde uno de tus dispositivos de confianza.',
        pendingApprovalsEmpty: 'Ahora no hay aprobaciones de dispositivos pendientes.',
        pendingApprovalsApprove: 'Aprobar',
        pendingApprovalsReject: 'Rechazar',
        pendingApprovalsKnownDevice: 'Dispositivo conocido',
        pendingApprovalsUnknownDevice: 'Dispositivo nuevo',
        trustedDevicesTitle: 'Dispositivos de confianza',
        trustedDevicesCopy: 'Revisa los navegadores y clientes que pueden iniciar sesión sin esperar una segunda aprobación.',
        trustedDevicesEmpty: 'Todavía no hay dispositivos de confianza para esta cuenta.',
        trustedDevicesCurrent: 'Dispositivo actual',
        trustedDevicesRevoke: 'Revocar dispositivo',
        trustedDevicesTrustedAt: 'Confiado',
        trustedDevicesUpdatedAt: 'Actualizado',
        trustedDevicesStatusTrusted: 'Confiado',
        trustedDevicesStatusPending: 'Pendiente',
        trustedDevicesStatusRevoked: 'Revocado',
        invitesTitle: 'Invitaciones de registro móvil',
        invitesCopy: 'Revisa invitaciones históricas ligadas al teléfono. Las cuentas nuevas deben crearse en la app móvil de Chattenger.',
        invitesEmpty: 'Todavía no hay invitaciones activas ni históricas.',
        invitesRevoke: 'Revocar invitación',
        invitesExpiresAt: 'Expira',
        invitesConsumedAt: 'Usada',
        invitesRevokedAt: 'Revocada',
        invitesStatusActive: 'Activa',
        invitesStatusConsumed: 'Usada',
        invitesStatusRevoked: 'Revocada',
    },
    landing: {
        ...EN.landing,
        eyebrow: 'Plataforma de comunicación segura',
        title: 'Chattenger',
        description:
            'Mensajería, llamadas, canales y trabajo en navegador en un solo producto moderno. Chattenger se está construyendo como una alternativa local, segura y confiable a los servicios extranjeros.',
        openLogin: 'Entrar',
        learnMore: 'Para inversores',
        badges: ['Rápido en móvil', 'Tiempo real', 'Listo para crecer'],
    },
    auth: {
        ...EN.auth,
        title: 'Chattenger',
        loginTitle: 'Entrar con código',
        loginDescription: 'Introduce tu número, confirma el código y continúa en Chattenger.',
        runtimeUnavailableTitle: 'Entorno no disponible',
        runtimeUnavailableDescription: 'El servidor no responde. El acceso puede fallar mientras se recupera el entorno.',
        methodLabel: 'Método de acceso',
        methodOtp: 'Código',
        methodQr: 'QR',
        phoneLabel: 'Teléfono',
        phonePlaceholder: 'Tu número',
        continue: 'Continuar',
        requesting: 'Enviando…',
        otpLabel: 'Código OTP',
        otpHint: 'Introduce el código de verificación.',
        callOtpHint: 'Responde la llamada e introduce el código que escuches.',
        smsOtpHint: 'Te enviaremos un SMS ahora. Introduce el código.',
        verify: 'Confirmar',
        verifying: 'Verificando…',
        resendCode: 'Reenviar código',
        resendIn: 'Reintentar en {seconds}s',
        retryCall: 'Llamar otra vez',
        retrySms: 'SMS otra vez',
        switchToCall: 'Probar llamada',
        switchToSms: 'Probar SMS',
        changePhone: 'Cambiar número',
        requestOtpFailed: 'No se pudo solicitar el OTP',
        verifyOtpFailed: 'No se pudo verificar el OTP',
        pendingDeviceApprovalExpiredStatus: 'Caducado',
        pendingDeviceApprovalRejectedStatus: 'Rechazado',
        pendingDeviceApprovalNotFoundStatus: 'No disponible',
        cantLogin: '¿No puedes entrar?',
        registrationEmailTitle: 'Confirma tu email',
        registrationEmailDescription: 'Introduce el código enviado a tu email antes de abrir la cuenta.',
        registrationEmailSentTo: 'Enviamos un código de 4 dígitos a {email}.',
        trustedDeviceBootstrapEmailTitle: 'Confirma este dispositivo de confianza',
        trustedDeviceBootstrapEmailDescription: 'Introduce el código del email para terminar el inicio de sesión en este dispositivo.',
        trustedDeviceBootstrapEmailSentTo: 'Enviamos un código de 4 dígitos a {email}.',
        mobileRegistrationRequired: 'Las cuentas nuevas deben crearse en la app móvil de Chattenger.',
        emailOtpLabel: 'Código de email',
        emailOtpHint: 'Introduce el código de 4 dígitos de tu email.',
        registrationRecoveryCodesTitle: 'Guarda tus códigos de recuperación',
        accountRecoveryTitle: 'Recuperar cuenta',
        accountRecoveryDescription: 'Introduce el código del teléfono y un código de recuperación para restablecer dispositivos.',
        recoveryCodeLabel: 'Código de recuperación',
        recoveryCodePlaceholder: 'CHTG-XXXX-XXXX-XXXX-XXXX',
        recoveryCodeHint: 'Usa un código de respaldo guardado antes.',
        accountRecoveryCodesTitle: 'Nuevos códigos de recuperación',
        recoveryCodesDescription: 'Estos códigos se muestran una sola vez. Guárdalos privados y sin conexión.',
        recoveryCodesWriteDown: 'Anota estos códigos. Son la única forma automática de recuperar la cuenta más tarde.',
        copyRecoveryCodes: 'Copiar códigos',
        emailRecoveryCodes: 'Enviar códigos',
        recoveryCodesCopied: 'Códigos copiados',
        recoveryCodesDeliveryTitle: 'Enviar códigos de recuperación',
        recoveryCodesDeliveryDescription:
            'Envía una copia a {email} o guarda una copia sin leer en tu buzón de Chattenger después de iniciar sesión.',
        recoveryCodesVerifiedEmailLabel: 'tu email verificado',
        recoveryCodesInboxCopyTitle: 'Crear una copia sin leer en el buzón de Chattenger',
        recoveryCodesInboxCopyDescription: 'Después de iniciar sesión, abre Mail y mantén el mensaje de códigos sin leer hasta que lo elijas.',
        sendRecoveryCodesEmail: 'Enviar por email',
        recoveryCodesEmailSending: 'Enviando…',
        recoveryCodesInboxCopyCreating: 'Creando tu copia en el buzón de Chattenger…',
        recoveryCodesEmailSent: 'Email enviado',
        recoveryCodesNextCountdown: 'Siguiente ({seconds})',
        qrEyebrow: 'Chattenger Web',
        qrTitle: 'Entrar con QR',
        qrDescription: 'Abre Chattenger en tu teléfono y confirma el acceso.',
        qrInstructionOne: 'Abre Chattenger.',
        qrInstructionTwo: 'Ve a Ajustes.',
        qrInstructionThree: 'Elige acceso web y escanea el código.',
        qrRefreshing: 'Actualizando código…',
        qrWaiting: 'Esperando confirmación desde tu teléfono…',
        qrExpired: 'Este código caducó. Genera un QR nuevo.',
        qrFailed: 'No se pudo cargar el QR.',
    },
    calls: {
        ...EN.calls,
        callEndedUnexpectedly: 'La llamada terminó de forma inesperada.',
        callParticipantUnavailable: 'No se pudo conectar la llamada porque no había nadie disponible.',
        callConnectionUnavailable: 'Las llamadas no están disponibles temporalmente. Inténtalo de nuevo en un minuto.',
        callParticipantBusy: 'La otra persona ya está en otra llamada.',
        callAnsweredElsewhere: 'Esta llamada fue respondida en otro dispositivo.',
        microphoneLabel: 'Micrófono',
        videoLabel: 'Cámara',
    },
    assetViewer: {
        dialogLabel: 'Visor de adjuntos',
        thumbnailsLabel: 'Elementos',
        open: 'Abrir',
        download: 'Descargar',
        downloadCurrent: 'Solo este archivo',
        downloadAll: 'Todos los archivos',
        downloadChoiceTitle: '¿Qué quieres descargar?',
        downloadChoiceDescription: 'Guarda solo el archivo actual o inicia la descarga de todos los adjuntos de este mensaje.',
        previous: 'Elemento anterior',
        next: 'Elemento siguiente',
        videoBadge: 'Vídeo',
        pdfBadge: 'PDF',
        fileBadge: 'Archivo',
    },
    assistant: {
        ...EN.assistant,
        navLabel: 'Asistente',
        eyebrow: 'Asistente',
        title: 'Preguntar al asistente de Chattenger',
        description: 'Usa esta superficie dedicada para el asistente. No se abre como chat normal ni como contacto buscable.',
        inputLabel: 'Prompt',
        inputPlaceholder: 'Pide ayuda con Chattenger, tu espacio de trabajo o una tarea web clara.',
        submit: 'Preguntar',
        submitting: 'Pensando…',
        pendingLabel: 'El asistente está escribiendo…',
        responseLabel: 'Respuesta',
        unavailableTitle: 'Asistente no disponible',
        unavailableCopy: 'El asistente no está disponible ahora. Inténtalo más tarde.',
        emptyCopy: 'Las respuestas dependen de que el proveedor backend esté habilitado.',
    },
    vpnPlusShareCode: {
        ...EN.vpnPlusShareCode,
        eyebrow: 'VPN+ compartido',
        title: 'Canjear datos VPN+ compartidos',
        description: 'Inicia sesión con tu cuenta existente de Chattenger para mover esta asignación VPN+ retenida a tu saldo.',
        codeLabel: 'Código',
        invalidCodeFallback: 'Código no válido',
        readyTitle: 'Listo para canjear',
        readyCopy: 'Este código solo se puede usar una vez. Chattenger lo verificará antes de cambiar tu saldo VPN+.',
        redeemAction: 'Canjear',
        redeemingAction: 'Canjeando…',
        redeemingTitle: 'Verificando código',
        redeemingCopy: 'Chattenger está validando este código VPN+.',
        redeemedTitle: 'Datos VPN+ añadidos',
        redeemedCopy: 'La asignación VPN+ compartida se añadió a tu cuenta.',
        expiredTitle: 'Código caducado',
        expiredCopy: 'Este código ya no está activo. Pide a quien lo envió que cree uno nuevo.',
        usedTitle: 'Código ya usado',
        usedCopy: 'Este código ya fue canjeado y no puede usarse otra vez.',
        cancelledTitle: 'Código cancelado',
        cancelledCopy: 'Quien lo envió canceló este código antes del canje.',
        invalidTitle: 'Código no disponible',
        invalidCopy: 'Este código no es válido o está mal formado.',
        featureOffTitle: 'El uso compartido de VPN+ no está disponible',
        featureOffCopy: 'El canje de códigos VPN+ no está habilitado ahora para esta cuenta.',
        unavailableTitle: 'No se pudo canjear',
        unavailableCopy: 'Chattenger no pudo canjear este código ahora. Inténtalo más tarde.',
        copyLink: 'Copiar enlace',
        copyLinkSuccess: 'Enlace VPN+ copiado.',
        copyLinkFailed: 'No se pudo copiar este enlace.',
    },
    dashboard: {
        ...EN.dashboard,
        jumpToLatest: 'Ir al final',
        messageMediaQueuedImage: 'Imagen en cola',
        messageMediaQueuedVideo: 'Vídeo en cola',
        messageMediaVideoBadge: 'Vídeo',
        messageMediaImageFallback: 'Imagen {index}',
        messageMediaVideoFallback: 'Vídeo {index}',
        messageMediaThumbnailFallback: 'Vista previa',
        circleVideoExpandAria: 'Expandir vídeo circular',
        circleVideoResumeAria: 'Reanudar vídeo circular',
        circleVideoCollapseAria: 'Contraer vídeo circular',
        circleVideoPlaceholder: 'La vista previa del vídeo circular se está cargando',
        circleVideoPaused: 'En pausa',
        circleVideoLivePreview: 'Vista previa en vivo',
        circleVideoUnmute: 'Activar sonido',
        circleVideoMute: 'Silenciar',
        circleVideoSeekHandleAria: 'Mover la posición del vídeo circular',
    },
    admin: {
        ...EN.admin,
        nav: {
            ...EN.admin.nav,
            mermaid: 'Editor Mermaid',
        },
        mermaid: {
            canvasLabel: 'Área de trabajo del diagrama',
            editorTab: 'Editor',
            errorLink: 'El enlace del diagrama no es válido o supera el límite seguro.',
            errorSyntax: 'No se pudo renderizar el diagrama. Corrige la sintaxis de Mermaid e inténtalo de nuevo.',
            fit: 'Ajustar diagrama',
            loading: 'Cargando editor…',
            pageDescription: 'Edita un diagrama privado en el navegador y comparte su estado acotado en la URL sin guardar el código en Chattenger.',
            pageEyebrow: 'Admin / Mermaid',
            pageTitle: 'Editor Mermaid',
            previewTab: 'Vista previa',
            ready: 'El diagrama está listo',
            rendering: 'Renderizando diagrama…',
            reset: 'Restablecer vista',
            share: 'Copiar enlace',
            shareCopied: 'Enlace copiado',
            shareUnavailable: 'No se puede copiar el enlace en este navegador.',
            sourceLabel: 'Código Mermaid',
            zoomIn: 'Acercar',
            zoomOut: 'Alejar',
        },
    },
    recordings: {
        eyebrow: 'Grabaciones',
        title: 'Tus sesiones owner-private de grabación de sala en todos los dispositivos.',
        listTitle: 'Grabaciones',
        noRecordingsEyebrow: 'Aún no hay grabaciones',
        noRecordingsTitle: 'Tus sesiones de grabación aparecerán aquí.',
        noRecordingsCopy: 'Inicia una grabación de llamada grupal en web o móvil y cada sesión privada aparecerá aquí por separado.',
        detailEyebrow: 'Detalle de la grabación',
        playback: 'Reproducción',
        downloadMp4: 'Descargar MP4',
        sceneManifest: 'Manifiesto de escena',
        newBadge: 'Nuevo',
        playbackPending: 'Esta grabación sigue esperando el fin de la llamada o el empaquetado final antes de poder reproducirse.',
        playbackFailed: 'Esta grabación falló durante la preparación del artefacto.',
        previewLabel: 'Vista previa',
        roomLabel: 'Sala #{roomId}',
        chatFallbackLabel: 'Chat {chatId}',
        createdLabel: 'Creado',
        roomStateLabel: 'Estado de la sala',
        roomStateEnded: 'Llamada finalizada',
        roomStateRecordingLive: 'Grabación en curso',
        roomStateNoActive: 'No hay grabación activa',
        roomActivityLabel: 'Actividad de la sala',
        roomActivityCount: 'Sesiones activas: {count}',
        roomActivityIdle: 'Inactiva',
        artifactLabel: 'Artefacto',
        durationLabel: 'Duración',
        durationSeconds: '{seconds}s',
        playbackReadyLabel: 'Listo para abrir',
        playbackPendingLabel: 'Todavía no está listo',
        details: 'Detalles',
        searchRecordingsLabel: 'Buscar grabaciones',
        filterAll: 'Todas',
        filterReady: 'Listas',
        filterActive: 'Activas',
        sortRecent: 'Recientes',
        sortChat: 'Chat',
        sortStatus: 'Estado',
        paginationPrevious: 'Anterior',
        paginationNext: 'Siguiente',
        paginationStatus: 'Página {current} de {total}',
        lastStartedLabel: 'Último inicio',
        pausedAtLabel: 'Pausado en',
        pendingRoomEndLabel: 'Fin de sala pendiente',
        sealedAtLabel: 'Sellado en',
        artifactReadyLabel: 'Artefacto listo',
        artifactFailedLabel: 'Artefacto fallido',
        sessionIdLabel: 'Id de sesión',
        ownerDeviceLabel: 'Dispositivo del propietario',
        ownerOtherDeviceLabel: 'Otro dispositivo #{deviceId}',
        artifactStatusLabel: 'Estado del artefacto',
        artifactDurationLabel: 'Duración del artefacto',
        artifactSizeLabel: 'Tamaño del artefacto',
        sizeMegabytes: '{size} MB',
        artifactErrorLabel: 'Error del artefacto',
        recordedIntervalsLabel: 'Intervalos grabados',
        timeRangePattern: '{start} → {end}',
        ownerPrivateRecordingTitle: 'Grabación owner-private',
        copiedLabel: 'Copiado',
        copyFailedLabel: 'Error al copiar',
        copyDetailsLabel: 'Copiar detalles',
        playPlaybackLabel: 'Reproducir',
        pausePlaybackLabel: 'Pausar',
        seekPlaybackLabel: 'Mover reproducción',
        rewindTenSecondsLabel: 'Retroceder 10 segundos',
        forwardTenSecondsLabel: 'Avanzar 10 segundos',
        enterFullscreenLabel: 'Abrir pantalla completa',
        exitFullscreenLabel: 'Salir de pantalla completa',
        processingTimelineLabel: 'Cronología de procesamiento',
        summaryRecordingLine: 'Grabación #{id}',
        summaryRoomLine: 'Sala #{roomId}',
        summaryStatusLine: 'Estado: {status}',
        summaryArtifactLine: 'Artefacto: {artifact}',
    },
};
